import { Category } from '../types/header';
import { getCurrentIdToken } from '../auth/AuthContext';

/**
 * API GatewayのベースエンドポイントURLを取得
 * 環境変数 VITE_LAMBDA_ENDPOINT から取得し、環境に応じてステージを追加
 */
const getApiEndpoint = (): string => {
  const endpoint = import.meta.env.VITE_LAMBDA_ENDPOINT;
  if (endpoint) {
    let cleanEndpoint = endpoint.endsWith('/') ? endpoint.slice(0, -1) : endpoint;
    
    const isCloudFront = cleanEndpoint.includes('cloudfront.net');
    
    if (isCloudFront) {
      return cleanEndpoint;
    }
    
    const urlMatch = cleanEndpoint.match(/amazonaws\.com(\/.*)?$/);
    const pathAfterAws = urlMatch ? (urlMatch[1] || '') : '';
    const hasStage = pathAfterAws.length > 0 && pathAfterAws !== '/';
    
    if (!hasStage) {
      if (import.meta.env.DEV) {
        cleanEndpoint = `${cleanEndpoint}/dev`;
      } else if (import.meta.env.PROD) {
        cleanEndpoint = `${cleanEndpoint}/prod`;
      }
    }
    
    return cleanEndpoint;
  }
  
  const baseUrl = 'https://5iu8q9zvu1.execute-api.us-east-1.amazonaws.com';
  if (import.meta.env.DEV) {
    return `${baseUrl}/dev`;
  } else if (import.meta.env.PROD) {
    return `${baseUrl}/prod`;
  }
  return `${baseUrl}/dev`;
};

/**
 * カテゴリー一覧を取得する（認証不要、公開エンドポイント）
 */
export interface GetCategoriesResponse {
  categories: Category[];
}

/**
 * 公開用のカテゴリー一覧を取得する（認証不要）
 */
export const getPublicCategories = async (): Promise<GetCategoriesResponse> => {
  const endpoint = getApiEndpoint();
  const url = `${endpoint}/categories`;
  
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      if (response.status === 400) {
        let errorMessage = 'リクエストが不正です';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 400 && response.status < 500) {
        let errorMessage = `リクエストエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 500) {
        let errorMessage = `サーバーエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = `サーバーエラー: ${errorData.message}`;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const responseData = await response.json();
    
    let data: GetCategoriesResponse;
    if (responseData.body) {
      data = typeof responseData.body === 'string' 
        ? JSON.parse(responseData.body) 
        : responseData.body;
    } else {
      data = responseData;
    }
    
    return data;
  } catch (error) {
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error('CORSエラー: API GatewayのCORS設定を確認してください。開発環境では、API GatewayでCORSを有効化する必要があります。');
    }
    
    throw error;
  }
};

/**
 * カテゴリー一覧を取得する（管理画面用、認証必要）
 */
export const getCategories = async (): Promise<GetCategoriesResponse> => {
  const endpoint = getApiEndpoint();
  // 管理画面用のエンドポイント
  const url = `${endpoint}/admin/categories`;
  
  try {
    const idToken = await getCurrentIdToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      if (response.status === 403) {
        let errorMessage = 'APIへのアクセスが拒否されました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message === 'Missing Authentication Token') {
            errorMessage = '認証トークンが不足しています。API Gatewayの設定を確認してください。';
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        const error = new Error(errorMessage);
        (error as any).isAuthError = true;
        throw error;
      } else if (response.status === 400) {
        let errorMessage = 'リクエストが不正です';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 400 && response.status < 500) {
        let errorMessage = `リクエストエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 500) {
        let errorMessage = `サーバーエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = `サーバーエラー: ${errorData.message}`;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const responseData = await response.json();
    
    let data: GetCategoriesResponse;
    if (responseData.body) {
      data = typeof responseData.body === 'string' 
        ? JSON.parse(responseData.body) 
        : responseData.body;
    } else {
      data = responseData;
    }
    
    return data;
  } catch (error) {
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error('CORSエラー: API GatewayのCORS設定を確認してください。開発環境では、API GatewayでCORSを有効化する必要があります。');
    }
    
    throw error;
  }
};

/**
 * カテゴリーを個別取得する
 */
export interface GetCategoryResponse {
  category: Category;
}

export const getCategory = async (categoryId: string): Promise<GetCategoryResponse> => {
  const endpoint = getApiEndpoint();
  // 管理画面用のエンドポイント
  const url = `${endpoint}/admin/categories/${encodeURIComponent(categoryId)}`;
  
  try {
    const idToken = await getCurrentIdToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      if (response.status === 403) {
        let errorMessage = 'APIへのアクセスが拒否されました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message === 'Missing Authentication Token') {
            errorMessage = '認証トークンが不足しています。API Gatewayの設定を確認してください。';
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        const error = new Error(errorMessage);
        (error as any).isAuthError = true;
        throw error;
      } else if (response.status === 404) {
        let errorMessage = 'カテゴリーが見つかりません';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status === 400) {
        let errorMessage = 'リクエストが不正です';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 400 && response.status < 500) {
        let errorMessage = `リクエストエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 500) {
        let errorMessage = `サーバーエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = `サーバーエラー: ${errorData.message}`;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const responseData = await response.json();
    
    let data: GetCategoryResponse;
    if (responseData.body) {
      data = typeof responseData.body === 'string' 
        ? JSON.parse(responseData.body) 
        : responseData.body;
    } else {
      data = responseData;
    }
    
    return data;
  } catch (error) {
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error('CORSエラー: API GatewayのCORS設定を確認してください。開発環境では、API GatewayでCORSを有効化する必要があります。');
    }
    
    throw error;
  }
};

/**
 * カテゴリーを作成する
 */
export interface CreateCategoryData {
  name: string;
  name_en?: string;
  slug?: string;
  is_visible?: boolean;
  order?: number;
}

export const createCategory = async (categoryData: CreateCategoryData): Promise<GetCategoryResponse> => {
  const endpoint = getApiEndpoint();
  // 管理画面用のエンドポイント
  const url = `${endpoint}/admin/categories`;
  
  try {
    const idToken = await getCurrentIdToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(categoryData),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      if (response.status === 400) {
        let errorMessage = 'リクエストが不正です';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status === 403) {
        let errorMessage = 'APIへのアクセスが拒否されました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message === 'Missing Authentication Token') {
            errorMessage = '認証トークンが不足しています。API Gatewayの設定を確認してください。';
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        const error = new Error(errorMessage);
        (error as any).isAuthError = true;
        throw error;
      } else if (response.status === 500) {
        let errorMessage = 'サーバーエラーが発生しました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = `サーバーエラー: ${errorData.message}`;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const responseData = await response.json();
    
    let data: GetCategoryResponse;
    if (responseData.body) {
      data = typeof responseData.body === 'string' 
        ? JSON.parse(responseData.body) 
        : responseData.body;
    } else {
      data = responseData;
    }
    
    return data;
  } catch (error) {
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error('CORSエラー: API GatewayのCORS設定を確認してください。');
    }
    
    throw error;
  }
};

/**
 * カテゴリーを更新する
 */
export interface UpdateCategoryData {
  name?: string;
  name_en?: string;
  slug?: string;
  is_visible?: boolean;
  order?: number;
}

export const updateCategory = async (categoryId: string, categoryData: UpdateCategoryData): Promise<GetCategoryResponse> => {
  const endpoint = getApiEndpoint();
  // 管理画面用のエンドポイント
  const url = `${endpoint}/admin/categories/${encodeURIComponent(categoryId)}`;
  
  try {
    const idToken = await getCurrentIdToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    
    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify(categoryData),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      if (response.status === 400) {
        let errorMessage = 'リクエストが不正です';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        // デバッグ用: リクエストボディとエラーレスポンスをログ出力
        console.error('Category update 400 error:', {
          categoryId,
          categoryData,
          errorText,
          requestBody: JSON.stringify(categoryData),
        });
        throw new Error(errorMessage);
      } else if (response.status === 403) {
        let errorMessage = 'APIへのアクセスが拒否されました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message === 'Missing Authentication Token') {
            errorMessage = '認証トークンが不足しています。API Gatewayの設定を確認してください。';
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        const error = new Error(errorMessage);
        (error as any).isAuthError = true;
        throw error;
      } else if (response.status === 404) {
        let errorMessage = 'カテゴリーが見つかりません';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status === 500) {
        let errorMessage = 'サーバーエラーが発生しました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = `サーバーエラー: ${errorData.message}`;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const responseData = await response.json();
    
    let data: GetCategoryResponse;
    if (responseData.body) {
      data = typeof responseData.body === 'string' 
        ? JSON.parse(responseData.body) 
        : responseData.body;
    } else {
      data = responseData;
    }
    
    return data;
  } catch (error) {
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error('CORSエラー: API GatewayのCORS設定を確認してください。');
    }
    
    throw error;
  }
};

/**
 * カテゴリーを削除する
 */
export interface DeleteCategoryResponse {
  message: string;
}

export const deleteCategory = async (categoryId: string): Promise<DeleteCategoryResponse> => {
  const endpoint = getApiEndpoint();
  // 管理画面用のエンドポイント
  const url = `${endpoint}/admin/categories/${encodeURIComponent(categoryId)}`;
  
  try {
    const idToken = await getCurrentIdToken();
    const headers: HeadersInit = {};
    
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      if (response.status === 403) {
        let errorMessage = 'APIへのアクセスが拒否されました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message === 'Missing Authentication Token') {
            errorMessage = '認証トークンが不足しています。API Gatewayの設定を確認してください。';
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        const error = new Error(errorMessage);
        (error as any).isAuthError = true;
        throw error;
      } else if (response.status === 404) {
        let errorMessage = 'カテゴリーが見つかりません';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status === 500) {
        let errorMessage = 'サーバーエラーが発生しました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = `サーバーエラー: ${errorData.message}`;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const responseData = await response.json();
    
    let data: DeleteCategoryResponse;
    if (responseData.body) {
      data = typeof responseData.body === 'string' 
        ? JSON.parse(responseData.body) 
        : responseData.body;
    } else {
      data = responseData;
    }
    
    return data;
  } catch (error) {
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error('CORSエラー: API GatewayのCORS設定を確認してください。');
    }
    
    throw error;
  }
};

